import CustomElement from '../core/custom.js'
import Emitter from '../core/emitter.js'
import Viewport from '../stores/viewport.js'
import App from '../stores/app.js'
import getOffsetTop from '../utilities/getOffsetTop.js'
import remapRange from '../utilities/remapRange.js'
import clamp from '../utilities/clamp.js'

import resolveConfig from 'tailwindcss/resolveConfig'
import customConfig from '../../../tailwind.config.js'
import { easeOutSine } from '../utilities/easings.js'

import CubicBezier from '../webgl/helpers/cubicBezier';
import { Container, Mesh, Program, IcoSphereGeometryBuffer } from 'beam';
import Box from '../webgl/Box';
import gridVertexShader from '../webgl/shaders/grid-vs.glsl'
import gridFragmentShader from '../webgl/shaders/grid-fs.glsl'
import Tween from '../webgl/helpers/Tween';

const tailwindConfig = resolveConfig(customConfig)

export default window.customElements.define(
  'block-intro',
  class extends CustomElement {
    isSmall = true
    ready = true

    scaleMin = 0.5
    scaleMax = 1

    constructor() {
      super()
      this._onResize = this.onResize.bind(this)
      this._onUpdate = this.onUpdate.bind(this)
      this._onTransitionInDone = this.onTransitionInDone.bind(this)

      this.onPointerMove = this.onPointerMove.bind(this);
      this.currGridsProgress = 0;
      this.lastFocusMode = 'text';
      this.focusMode = 'text';
      this.rY = 0;
      this.pX = 0;
      this.pZ = 0;
      this.contentX = 0;
      this.contentOp = 1;
    }

    connectedCallback() {
      super.connectedCallback()

      this.firstElementChild.style.opacity   = 0;

      Emitter.on('resize', this._onResize)
      Emitter.on('update', this._onUpdate)
      Emitter.on('transition-in:done', this._onTransitionInDone)

      this.__isConnected__ = true;
      let node = this;
      let i=0;
      while (node.parentNode && i<100) { 
        if (node.parentNode.tagName == 'WEBGL-SCENE') {
          this.$glScene = node.parentNode;
          break;
        }
        node = node.parentNode;
        i++;
      }

      if (!this.$glScene) {
        console.warn('missing <gl-scene> parent node');
        return
      }
      
      this._onResize()
      requestAnimationFrame(()=>{
        this.init();
      });
    }

    disconnectedCallback() {
      super.disconnectedCallback()

      Emitter.off('resize', this._onResize)
      Emitter.off('update', this._onUpdate)
      Emitter.off('transition-in:done', this._onTransitionInDone)


      this.$glScene.removeChild( this.container )
      this.$glScene.removeChild( this.icoSphere )
      this.$glScene.unregisterBox(this.box);
    }

    onResize() {
      this._offsetTop = getOffsetTop(this)
      this._height = this.offsetHeight
      this._initScale = (Viewport.width / (2 * parseInt(tailwindConfig.theme.spacing.wrapper) + Viewport.width)) * 0.5
      this.isPortrait = window.innerWidth < 1023;
    }

    onTransitionLeave() {
      this.ready = false
    }

    onTransitionInDone() {
      this.onResize()
    }

    onBreakpoint({ viewport }) {
      this.isSmall = viewport.to('lg')
      
      this.scaleMin = this.isSmall ? 0.5 : 0.25
      this.scaleMax = this.isSmall ? 1 : 1.25

    }

    init() {
        this.renderer = this.$glScene.renderer;
        this.container = new Container();
        this.$glScene.addChild(this.container);

        this.data = {
          image_src:  this.getAttribute('image-src'),
          video_src:  this.getAttribute('video-src') || `/wp-content/themes/wp-base-theme/dist/assets/videos/banner_1.mp4`,
        }

        this.box = new Box(this.renderer, {
          src: this.data.video_src, 
          maskChanel: [1,0,0],
          boxScale:   0.5,
        });
        this.box.container.scale[0] = 0
        this.box.container.scale[1] = 0
        this.box.container.scale[2] = 0
        this.container.add(this.box.container)

        this.$glScene.registerBox(this.box);
        
        const icoSphere = new Mesh();
        icoSphere.geometry = new IcoSphereGeometryBuffer(this.renderer.gl, {
          radius:         2000, 
          details:        6,
        });
        icoSphere.material = new Program(this.renderer.gl, {
          vertexShader:   gridVertexShader,
          fragmentShader: gridFragmentShader,
          type:           this.renderer.gl.POINTS,
          uniforms: {
            uPointScale:   1,
            uInfiniteGrid: 0,
            uSkipOpacity:  1,
          }
        });
        this.icoSphere = icoSphere;
        this.$glScene.addChild(icoSphere);

        document.addEventListener('mousemove', this.onPointerMove);
        // document.addEventListener('touchmove', this.onPointerMove, {passive: false});
    }

    toggleMode(focusMode) {

        let startrY = this.rY;
        let startpX = this.pX;
        let startpZ = this.pZ;
        let startContentOp = this.contentOp;
        let startContentX = this.contentX;


        let endY         = focusMode  == 'media' ? -0.5 : 0;
        let endX         = focusMode  == 'media' ? window.innerWidth * 0.25 * 0.5 : window.innerWidth * 0.25 * -0.5;
        let endZ         = focusMode  == 'media' ? 300 : 0;
        let endContentX  = focusMode  == 'media' ?  30 : 0;
        let endContentOp = focusMode  == 'media' ?   1 : 1;

        this.tween?.destroy();
        this.tween = Tween({e:0}, {
          to: {e:1},
          delay: 0, 
          easing:  [0.690, 0.000, 0.090, 1.000],
          duration: 2,
          step: e => {
            this.rY = startrY + (endY - startrY) * e;
            this.pX = startpX + (endX - startpX) * e;
            this.pZ = startpZ + (endZ - startpZ) * e;
            this.contentX = startContentX + (endContentX - startContentX) * e;
            this.contentOp = startContentOp + (endContentOp - startContentOp) * e;
          },
        });

    }

    onPointerMove(e) {

      if (window.innerWidth < 600) {
        let focusMode = 'text';
        if (this.lastFocusMode !== focusMode) {
          this.lastFocusMode = focusMode;
          this.toggleMode(focusMode)
        }
        return
      }


      
      const pointerEvent = e.touches ? e.touches[0] : e;
      let focusMode;
      if (pointerEvent.pageX < window.innerWidth / 2) {
        focusMode = 'media'
      }
      else {
        focusMode = 'text'
      }
      if (this.lastFocusMode !== focusMode) {
        this.lastFocusMode = focusMode;
        this.toggleMode(focusMode)
      }
    }
    
    onUpdate() {
      if (!this.ready) return

      this.progress = remapRange(
        this._offsetTop,
        this._offsetTop + Viewport.height * 2,
        0,
        1,
        App.lenis.animatedScroll + Viewport.height
      )
      // from entering at 0 to exiting at 1
      this.progress = clamp(this.progress, 0, 1)

      // from entering at 0 going to 1 then back to exiting at 0
      this.progressReversed = this.progress * 2
      if (this.progress > 0.5) this.progressReversed = 2 - this.progress * 2

      this._scale = remapRange(
        0,
        1,
        this.scaleMin,
        this.scaleMax,
        easeOutSine(this.progress)
      )

      // this._translateY = remapRange(
      //   0,
      //   1,
      //   -Viewport.height,
      //   Viewport.height,
      //   this.progress
      // )

      // this.firstElementChild.style.transform = `scale(${this._scale})`
      // this.firstElementChild.style.opacity = easeOutSine(this.progressReversed)

      
      
      const progress2      = Math.max(0, Math.min(1, (this.$glScene._scroll - window.innerHeight) / (window.innerHeight )));
      const easedProgress2 = CubicBezier(0.775, 0.065, 0.345, 0.880, progress2, 1 );
      
      const progress       = Math.max(0, Math.min(1, (App.lenis.animatedScroll ) / (window.innerHeight )));
      const easedProgress  = CubicBezier( 0.775, 0.065, 0.345, 0.880, progress, 1 );
      
      const sphereIntroOpacity  =  Math.min( 1, Math.max(0, (easedProgress - 0.5) / 0.5) );

      this._easedScroll = this._easedScroll || 0;
      this._easedScroll    += ((easedProgress ) - this._easedScroll) * 0.07; 

      this._easedScroll2 = this._easedScroll2 || 0;
      this._easedScroll2    += ((easedProgress2 ) - this._easedScroll2) * 0.07; 

      const progress3      = Math.max(0, Math.min(1, (App.lenis.animatedScroll - window.innerHeight) / (window.innerHeight )));
      const easedProgress3 = CubicBezier(0.430, 0.195, 0.020, 1.000, progress3, 1 );
    
      this.firstElementChild.style.transform = `translateX(${this.contentX}%) scale(${ 0.15 + 0.85 * this._easedScroll + this._easedScroll2 * 3}) translateZ(0)`;
      this.firstElementChild.style.opacity   = (this._easedScroll - easedProgress3) * this.contentOp;


      if (!this.box) {
        return;
      }

      this.currGridsProgress += ( ( ( easedProgress + easedProgress2) ) - this.currGridsProgress) * 0.1;

      this.box.boxScale = (window.innerWidth * 0.5  ) / 200 ;

      this.box.container.scale[0]    = (window.innerWidth * (this.isPortrait ? 0.9 : 0.5)  ) / 200 * this.currGridsProgress;
      this.box.container.scale[1]    = (window.innerWidth * (this.isPortrait ? 0.9 : 0.5)  ) / 200 * this.currGridsProgress;
      this.box.container.scale[2]    = (window.innerWidth * (this.isPortrait ? 0.9 : 0.5)  ) / 200 * this.currGridsProgress;
     
      this.box.container.position[0] = (window.innerWidth * 0.25 * (this.isPortrait ? 0 : -1)) * this.currGridsProgress +  this.pX;
      this.box.container.position[1] = (window.innerHeight * 0.2 * (this.isPortrait ? 1 : 0)) * this.currGridsProgress 
      

      this.box.container.rotation[1] = (0.5 + this.currGridsProgress * 0.5) * (this.isPortrait?0.5 : 1) + this.rY;
      this.box.container.position[2] = -window.innerHeight * 6  + (this.currGridsProgress) * window.innerHeight * 6 + this.pZ

      this.box.container.updateMatrix();
      this.box.container.updateWorldMatrix();

      this.icoSphere.material.uniforms.uPointScale.value = sphereIntroOpacity;
      this.icoSphere.position[2] =  -window.innerHeight * 6  + (this.currGridsProgress) * window.innerHeight * 6
      this.icoSphere.rotation[1] += ((0) - this.icoSphere.rotation[1]) * 0.1;
      this.icoSphere.updateMatrix()
      this.icoSphere.updateWorldMatrix()

      if (this.currGridsProgress > 0.5 && this.currGridsProgress < 1.5) {
        this.box.play();
      }
      else {
        this.box.pause();
      }
      

    }
  }
)
